var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { JobsContext } from 'libs.jobs.react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { useAddLocalizationImportNotification } from './use_add_localization_import_notification.hook';
export function useLocalizationImport() {
    const { post } = useApiClient(ApiClientService.WEB);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const { handleAddNotification } = useAddLocalizationImportNotification();
    const { mutate: importLocalization, isPending } = useMutation({
        mutationKey: ['translation', 'import'],
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ formData, requiredIds, supportedLocales, }) {
            const idPairs = (requiredIds === null || requiredIds === void 0 ? void 0 : requiredIds.map((id) => `ids[]=${id}`)) || [];
            const localePairs = (supportedLocales === null || supportedLocales === void 0 ? void 0 : supportedLocales.map((locale) => `locales[]=${locale}`)) || [];
            const q = [...idPairs, ...localePairs].join('&');
            if (!firebaseToken) {
                addNotification({
                    title: translate('Network error: please try again in a moment'),
                    type: 'error',
                });
                return;
            }
            return yield post(`/api/translation/import?${q}`, formData, {
                // Pass firebase token, don't pass medable token
                withCredentials: false,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${firebaseToken}`,
                },
            });
        }),
        onSettled: (data, error) => {
            var _a, _b, _c;
            const success = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.success) || ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.success);
            handleAddNotification(success, !!error);
        },
        retry: 1,
    });
    return {
        importLocalization,
        isPending,
    };
}
export function useLocalizationImportV2({ onImport, studyId, onStudyConfigSynced, }) {
    const { post } = useApiClient(ApiClientService.WEB);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const jobsContext = useContext(JobsContext);
    const { handleAddNotification } = useAddLocalizationImportNotification();
    const handleJob = (jobData, hasError = false) => {
        var _a, _b, _c, _d;
        const success = (_a = jobData === null || jobData === void 0 ? void 0 : jobData.params) === null || _a === void 0 ? void 0 : _a.success;
        const errors = (_b = jobData === null || jobData === void 0 ? void 0 : jobData.params) === null || _b === void 0 ? void 0 : _b.errors;
        if (Array.isArray(success)) {
            handleAddNotification(success, Array.isArray(errors) && errors.length > 0);
            onImport === null || onImport === void 0 ? void 0 : onImport(success);
            // The study import job updates the draft study config's l10n map server-side;
            // hand the new id back so the caller can reload its in-memory study.
            if (studyId) {
                onStudyConfigSynced === null || onStudyConfigSynced === void 0 ? void 0 : onStudyConfigSynced((_c = jobData === null || jobData === void 0 ? void 0 : jobData.params) === null || _c === void 0 ? void 0 : _c.l10nMapResourceId);
            }
        }
        if (Array.isArray(errors)) {
            errors.forEach((error) => {
                var _a, _b;
                addNotification({
                    title: translate((_a = error === null || error === void 0 ? void 0 : error.title) !== null && _a !== void 0 ? _a : 'Error'),
                    subtitle: translate((_b = error === null || error === void 0 ? void 0 : error.subtitle) !== null && _b !== void 0 ? _b : 'An unknown error occurred'),
                    type: 'error',
                });
            });
        }
        else if (hasError) {
            addNotification({
                title: translate((_d = jobData === null || jobData === void 0 ? void 0 : jobData.message_key) !== null && _d !== void 0 ? _d : 'Error'),
                type: 'error',
            });
        }
    };
    const { mutate: importLocalization, isPending } = useMutation({
        mutationKey: ['translation', 'import'],
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ files, requiredIds, supportedLocales, }) {
            if (!firebaseToken) {
                addNotification({
                    title: translate('Network error: please try again in a moment'),
                    type: 'error',
                });
                return;
            }
            const endpoint = studyId ? '/api/v2/translation/study/import' : '/api/v2/translation/import';
            const body = studyId
                ? { studyId, files, import_scope: requiredIds, supportedLocales }
                : { files, import_scope: requiredIds, supportedLocales };
            return yield post(endpoint, body, {
                withCredentials: false,
                headers: {
                    Authorization: `Bearer ${firebaseToken}`,
                },
            });
        }),
        onSuccess: (data) => {
            var _a;
            addNotification({
                title: translate('Import started'),
                subtitle: translate("Your import is processing. We'll display a notification here once the import is ready."),
                type: 'info',
            });
            const jobId = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.jobId;
            if (jobId && jobsContext) {
                jobsContext.addJobHandler(jobId, {
                    onDone: (completion_message) => {
                        handleJob(completion_message);
                    },
                    onError: (error_message) => {
                        handleJob(error_message, true);
                    },
                });
            }
        },
        onError: (error) => {
            addNotification({
                title: translate('Import failed'),
                subtitle: error.message ? translate(error.message) : translate('An unknown error occurred'),
                type: 'error',
            });
        },
    });
    return {
        importLocalization,
        isPending,
    };
}
//# sourceMappingURL=localization_import.hook.js.map